"use client";

import { motion } from "framer-motion";
import {
  Headphones,
  type LucideIcon,
  MessageSquare,
  Users,
} from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Capability {
  label: string;
  icon: LucideIcon;
  title: string;
  description: string;
  proof: string;
}

const capabilities: Capability[] = [
  {
    label: "Hire",
    icon: Users,
    title: "Hire human employees on demand",
    description:
      "Your agent sources identity-verified experts for real work: code review, research, judgment calls, physical-evidence tasks. Matched, screened, and verified on long-horizon jobs, and you pay only on completion.",
    proof: "200+ specialized domains, paid on verified completion",
  },
  {
    label: "Train",
    icon: MessageSquare,
    title: "Train and evaluate your models",
    description:
      "Launch preference, rating, and red-teaming tasks mid-run. RLHF, reward modeling, and release-gating evals get verified human signal back as structured JSON, ready for your training loop.",
    proof: "Structured results with verification provenance",
  },
  {
    label: "Listen",
    icon: Headphones,
    title: "Hear from real customers",
    description:
      "Recruit verified users and run AI-moderated interviews and surveys without leaving the workflow. When your agent hits ambiguity, it escalates to a human and feeds the answer back into context.",
    proof: "Verified humans, not crowdworkers or synthetic profiles",
  },
];

/**
 * Capability cards that float up over the bottom of the blue hero, mirroring
 * the root landing page's opportunity-card layout: a responsive grid pulled up
 * with a negative margin, brand-blue tinted shadow, and a staggered fade-up.
 */
export function MCPCapabilities() {
  return (
    <section className="relative z-10 -mt-28 pb-12 md:-mt-36">
      <Container>
        <div className="grid grid-cols-1 gap-4 px-1 py-2 sm:grid-cols-2 sm:gap-[14px] lg:grid-cols-3">
          {capabilities.map((capability, i) => (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: i * 0.1, ease: "easeOut" }}
              className="border-marketing-border bg-marketing-cream-light group flex h-full flex-col rounded-[20px] border p-6 shadow-[0_4px_20px_rgba(0,6,183,0.10),_0_2px_6px_rgba(0,6,183,0.06)] transition-[translate,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,6,183,0.16),_0_3px_10px_rgba(0,6,183,0.08)] md:p-7"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="bg-marketing-card-blue rounded-[10px] px-2.5 py-1.5">
                  <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
                    {capability.label}
                  </p>
                </div>
                <div className="text-marketing-blue border-marketing-border bg-marketing-cream flex h-9 w-9 items-center justify-center rounded-full border transition-transform group-hover:scale-105">
                  <capability.icon className="h-4 w-4" strokeWidth={2} />
                </div>
              </div>

              <h3 className="text-marketing-text mb-3 text-xl font-medium tracking-tight">
                {capability.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 flex-1 text-[15px] leading-relaxed">
                {capability.description}
              </p>

              <div className="border-marketing-border-light flex items-center gap-2.5 border-t pt-4">
                <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                <p className="text-marketing-text text-sm font-medium">
                  {capability.proof}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
